import { useRollbar } from "@rollbar/react";
import { useMemo } from "react";

import { getWorkbookDataHelper } from "api/api_helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useInterval } from "hooks/use_interval";
import { BreakoutRoomType, BreakoutType, ResourceType } from "shared/types";
import { classroomSelectors } from "store";

const POLLING_INTERVAL = 5000;

function WorkbookDataController({
  breakout,
  breakoutRooms,
  allBreakoutNotes,
}: {
  breakout: BreakoutType;
  breakoutRooms: BreakoutRoomType[];
  allBreakoutNotes: ResourceType[];
}) {
  const dispatch = useAppDispatch();
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const orgId = useAppSelector(classroomSelectors.selectOrgId());
  const rollbar = useRollbar();

  const resourceUuidToBreakoutRoomIdMap = useMemo(() => {
    const map = new Map<string, string>();
    breakoutRooms.forEach((breakoutRoom) => {
      const breakoutNotesForRoom = allBreakoutNotes.find((resource) =>
        breakoutRoom.resource_ids.includes(resource.id),
      );
      if (breakoutNotesForRoom?.external_id) {
        map.set(breakoutNotesForRoom.external_id, breakoutRoom.id);
      }
    });
    return map;
  }, [breakoutRooms, allBreakoutNotes]);

  const sampleResourceUuid = useMemo(() => {
    for (const key of resourceUuidToBreakoutRoomIdMap.keys()) {
      if (key) return key;
    }
    return undefined;
  }, [resourceUuidToBreakoutRoomIdMap]);

  // Poll workbook data from SLE (original logic)
  useInterval(async () => {
    if (!orgId || !sampleResourceUuid) return;
    try {
      await getWorkbookDataHelper(
        sampleResourceUuid, // any uuid fetches all workbooks; the map dispatches each to its room
        resourceUuidToBreakoutRoomIdMap,
        breakout.id,
        classId,
        orgId,
        breakout.share_resource_between_groups,
        breakoutRooms[0].flavor,
        dispatch,
      );
    } catch (error) {
      rollbar.error("[getWorkbookDataHelper] getWorkbookData failed", error as Error);
    }
  }, POLLING_INTERVAL);

  return null;
}

export { WorkbookDataController };
